import { EventStore, type InitFlowComponent, useMeFavouritePartner, useSettings, useSubscribe } from '@capsicum/common';
import { useAtom, useAtomValue } from 'jotai';
import { type ComponentProps, type ComponentType, useCallback, useEffect, useState } from 'react';
import { match } from 'ts-pattern';

import { isRegisteredSuccessAtom, showFavoriteTeamDrawer } from '../atoms';

export type TTeamCheckerFlowProps = {
	onProceed: () => void;
};

export type UseFavouriteTeamCheckerFlow = Pick<
	ComponentProps<InitFlowComponent>,
	'fallback' | 'isReactive' | 'next'
> & {
	LoginCheckerFlow: ComponentType<TTeamCheckerFlowProps>;
	RegistrationCheckerFlow: ComponentType<TTeamCheckerFlowProps>;
};

export const useFavouriteTeamCheckerFlow = ({
	fallback,
	isReactive,
	LoginCheckerFlow,
	next,
	RegistrationCheckerFlow
}: UseFavouriteTeamCheckerFlow) => {
	const [isVisible, setVisibility] = useState(false);
	const [flow, setFlow] = useState<'LOGIN' | 'REGISTER'>();
	const isRegisteredSuccess = useAtomValue(isRegisteredSuccessAtom);
	const [showFavoriteTeam, setShowFavoriteTeam] = useAtom(showFavoriteTeamDrawer);
	const { favoritePartner, isFavouritePartnerLoading } = useMeFavouritePartner({ enabled: flow === 'LOGIN' });

	const {
		areSettingsLoading,
		settings: { isTeamSelectorInitFlowDisabled }
	} = useSettings();

	const isLoading = areSettingsLoading || isFavouritePartnerLoading;

	useSubscribe([EventStore.ON_LOGOUT, EventStore.SESSION_EXPIRED], () => {
		const timeoutID = setTimeout(() => setShowFavoriteTeam(true), 500);

		return () => clearTimeout(timeoutID);
	});

	useEffect(() => {
		if (flow === undefined) setFlow(isRegisteredSuccess ? 'REGISTER' : 'LOGIN');
	}, [flow, isRegisteredSuccess]);

	useEffect(() => {
		if (flow === 'REGISTER') setShowFavoriteTeam(false);
	}, [flow, setShowFavoriteTeam]);

	useEffect(() => {
		if (!flow) return;

		setVisibility(
			!isLoading &&
				!isTeamSelectorInitFlowDisabled &&
				(flow === 'REGISTER' || (flow === 'LOGIN' && showFavoriteTeam && !favoritePartner))
		);
	}, [flow, isLoading, favoritePartner, showFavoriteTeam, isTeamSelectorInitFlowDisabled]);

	useEffect(() => {
		if (!isReactive) next();
	}, [next, isReactive]);

	const onProceed = useCallback(() => {
		setVisibility(false);
		setShowFavoriteTeam(false);
	}, [setShowFavoriteTeam]);

	return match({
		isReactive,
		isVisible
	})
		.with(
			{
				isReactive: false
			},
			() => fallback
		)
		.with(
			{
				isVisible: false
			},
			() => fallback
		)
		.otherwise(() =>
			flow === 'LOGIN' ? (
				<LoginCheckerFlow onProceed={onProceed} />
			) : (
				<RegistrationCheckerFlow onProceed={onProceed} />
			)
		);
};
